import React, { useState, useEffect, useCallback } from 'react';
import AppShell from '../../components/layout/AppShell';
import api from '../../services/api';
import { fmtDT, fmtPKR, fmtDate } from '../../utils/helpers';
import { Spinner, EmptyState, Modal } from '../../components/common';
import toast from 'react-hot-toast';
import { Plus, Trash2 } from 'lucide-react';

// ── General Ledger ────────────────────────────────────────────────────────────
export function GeneralLedgerPage() {
  const [entries, setEntries] = useState([]);
  const [totals, setTotals]   = useState({});
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [filters, setFilters] = useState({ accountCode:'', fromDate:'', toDate:'' });
  const [form, setForm] = useState({ accountCode:'', debit:'', credit:'', referenceType:'', description:'' });
  const set = k => e => setForm(f=>({...f,[k]:e.target.value}));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.accountCode) params.append('accountCode', filters.accountCode);
      if (filters.fromDate)    params.append('fromDate', filters.fromDate);
      if (filters.toDate)      params.append('toDate', filters.toDate);
      const r = await api.get(`/finance/gl?${params}&limit=100`).then(r=>r.data);
      if (r.success) { setEntries(r.data); setTotals(r.totals||{}); }
    } finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const addEntry = async () => {
    if (!form.accountCode) { toast.error('Account code required'); return; }
    if (!form.debit && !form.credit) { toast.error('Enter debit or credit amount'); return; }
    try {
      const r = await api.post('/finance/gl', form).then(r=>r.data);
      if (r.success) { toast.success('GL entry recorded'); setShowAdd(false); load(); }
    } catch(e) { toast.error(e.response?.data?.message||'Failed'); }
  };

  const ACCOUNT_CODES = ['FREIGHT_REVENUE','COD_COLLECTIONS','INSURANCE_FEES','TAX_PAYABLE','ACCOUNTS_RECEIVABLE','CASH','BANK','REFUNDS','OPERATING_EXPENSE'];

  return (
    <AppShell title="General Ledger" actions={
      <button className="btn btn-primary btn-sm" onClick={()=>setShowAdd(true)}><Plus size={14}/> Add Entry</button>
    }>
      <div className="stat-grid" style={{ gridTemplateColumns:'repeat(3,1fr)', marginBottom:20 }}>
        <div className="stat-card">
          <div className="stat-label">Total Debits</div>
          <div className="stat-value" style={{ fontSize:'1.1rem', color:'var(--danger)' }}>{fmtPKR(totals.total_debits)}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Total Credits</div>
          <div className="stat-value" style={{ fontSize:'1.1rem', color:'var(--success)' }}>{fmtPKR(totals.total_credits)}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Balance</div>
          <div className="stat-value" style={{ fontSize:'1.1rem' }}>{fmtPKR(Math.abs((totals.total_debits||0)-(totals.total_credits||0)))}</div>
        </div>
      </div>

      <div className="filters-bar">
        <select className="form-control" style={{ width:220 }} value={filters.accountCode} onChange={e=>setFilters(f=>({...f,accountCode:e.target.value}))}>
          <option value="">All Accounts</option>
          {ACCOUNT_CODES.map(a=><option key={a} value={a}>{a.replace(/_/g,' ')}</option>)}
        </select>
        <input className="form-control" type="date" style={{ width:150 }} value={filters.fromDate} onChange={e=>setFilters(f=>({...f,fromDate:e.target.value}))} />
        <input className="form-control" type="date" style={{ width:150 }} value={filters.toDate}   onChange={e=>setFilters(f=>({...f,toDate:e.target.value}))} />
        <button className="btn btn-outline btn-sm" onClick={load}>↻</button>
      </div>

      <div className="card">
        <div className="table-wrap">
          {loading ? <Spinner /> : entries.length ? (
            <table>
              <thead><tr><th>Date</th><th>Account</th><th>Description</th><th>Reference</th><th style={{ textAlign:'right' }}>Debit</th><th style={{ textAlign:'right' }}>Credit</th><th>Posted By</th></tr></thead>
              <tbody>
                {entries.map(e=>(
                  <tr key={e.entry_id}>
                    <td style={{ fontFamily:'monospace', fontSize:'.8rem' }}>{fmtDT(e.posted_at)}</td>
                    <td><span className="badge badge-navy" style={{ fontSize:'.68rem' }}>{e.account_code}</span></td>
                    <td style={{ fontSize:'.85rem' }}>{e.description||'—'}</td>
                    <td style={{ fontSize:'.75rem', color:'var(--gray-400)' }}>{e.reference_type||'—'}</td>
                    <td style={{ textAlign:'right', color:'var(--danger)', fontWeight:e.debit>0?600:400 }}>{e.debit>0?fmtPKR(e.debit):'—'}</td>
                    <td style={{ textAlign:'right', color:'var(--success)', fontWeight:e.credit>0?600:400 }}>{e.credit>0?fmtPKR(e.credit):'—'}</td>
                    <td style={{ fontSize:'.8rem' }}>{e.actor_name||'system'}</td>
                    <td>
                      <button className="btn btn-danger btn-sm" style={{ fontSize:'.7rem' }}
                        onClick={async()=>{ if(!window.confirm('Void this GL entry? This will create a reversal.')) return; try{ await api.delete('/finance/gl/'+e.entry_id); toast.success('Entry voided'); load(); }catch(ex){ toast.error(ex.response?.data?.message||'Cannot void posted entry'); } }}>
                        <Trash2 size={11}/>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ fontWeight:700, background:'var(--gray-50)' }}>
                  <td colSpan={4} style={{ textAlign:'right', fontSize:'.85rem' }}>Totals</td>
                  <td style={{ textAlign:'right', color:'var(--danger)' }}>{fmtPKR(totals.total_debits)}</td>
                  <td style={{ textAlign:'right', color:'var(--success)' }}>{fmtPKR(totals.total_credits)}</td>
                  <td/>
                </tr>
              </tfoot>
            </table>
          ) : <EmptyState icon="📒" title="No GL entries" message="Use the Add Entry button to record a journal entry." />}
        </div>
      </div>

      <Modal open={showAdd} onClose={()=>setShowAdd(false)} title="New GL Entry">
        <div className="modal-body">
          <div className="form-group">
            <label className="form-label" htmlFor="gl-acc">Account Code <span className="req">*</span></label>
            <select id="gl-acc" className="form-control" value={form.accountCode} onChange={set('accountCode')}>
              <option value="">— Select account —</option>
              {ACCOUNT_CODES.map(a=><option key={a} value={a}>{a.replace(/_/g,' ')}</option>)}
            </select>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label" htmlFor="gl-dr">Debit (PKR)</label>
              <input id="gl-dr" className="form-control" type="number" step="0.01" min="0" placeholder="0.00" value={form.debit} onChange={set('debit')} />
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="gl-cr">Credit (PKR)</label>
              <input id="gl-cr" className="form-control" type="number" step="0.01" min="0" placeholder="0.00" value={form.credit} onChange={set('credit')} />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label" htmlFor="gl-ref">Reference Type</label>
            <select id="gl-ref" className="form-control" value={form.referenceType} onChange={set('referenceType')}>
              <option value="">— None —</option>
              {['invoice','payment','cod_remittance','refund','adjustment','manual'].map(r=><option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label" htmlFor="gl-desc">Description</label>
            <input id="gl-desc" className="form-control" placeholder="Journal entry description" value={form.description} onChange={set('description')} />
          </div>
          {form.debit && form.credit && parseFloat(form.debit)>0 && parseFloat(form.credit)>0 && (
            <div className="alert alert-warning">Both debit and credit entered — ensure this is intentional for a compound entry.</div>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={()=>setShowAdd(false)}>Cancel</button>
          <button className="btn btn-primary" onClick={addEntry}>Post Entry</button>
        </div>
      </Modal>
    </AppShell>
  );
}

// ── Credit Aging ──────────────────────────────────────────────────────────────
export function AgingReportPage() {
  const [data, setData]     = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/finance/aging').then(r=>r.data).then(r=>{ if(r.success) setData(r.data); }).finally(()=>setLoading(false));
  }, []);

  const totals = data.reduce((a, r) => ({
    current: a.current + parseFloat(r.current_due||0),
    d30:     a.d30     + parseFloat(r.overdue_30||0),
    d60:     a.d60     + parseFloat(r.overdue_60||0),
    d90:     a.d90     + parseFloat(r.overdue_90||0),
    d120:    a.d120    + parseFloat(r.overdue_120plus||0),
    total:   a.total   + parseFloat(r.total_outstanding||0),
  }), { current:0, d30:0, d60:0, d90:0, d120:0, total:0 });

  return (
    <AppShell title="Credit Aging Report">
      {/* Summary buckets */}
      <div className="stat-grid" style={{ gridTemplateColumns:'repeat(5,1fr)', marginBottom:20 }}>
        {[['Current',totals.current,'var(--info)'],['1–30 days',totals.d30,'var(--warning)'],['31–60 days',totals.d60,'var(--amber)'],['61–90 days',totals.d90,'var(--danger)'],['90+ days',totals.d120,'#7f1d1d']].map(([l,v,c])=>(
          <div key={l} className="stat-card">
            <div className="stat-label">{l}</div>
            <div className="stat-value" style={{ fontSize:'1rem', color:c }}>{fmtPKR(v)}</div>
          </div>
        ))}
      </div>

      <div className="card">
        <div className="table-wrap">
          {loading ? <Spinner /> : data.length ? (
            <table>
              <thead>
                <tr><th>Customer</th><th>Invoices</th><th>Current</th><th>1–30 days</th><th>31–60 days</th><th>61–90 days</th><th>90+ days</th><th>Total</th></tr>
              </thead>
              <tbody>
                {data.map(r=>(
                  <tr key={r.customer_id}>
                    <td>
                      <div style={{ fontWeight:600 }}>{r.company_name||r.name}</div>
                      <div className="text-sm text-muted">{r.email}</div>
                    </td>
                    <td>{r.invoice_count}</td>
                    <td>{fmtPKR(r.current_due)}</td>
                    <td style={{ color:r.overdue_30>0?'var(--warning)':undefined }}>{fmtPKR(r.overdue_30)}</td>
                    <td style={{ color:r.overdue_60>0?'var(--amber)':undefined }}>{fmtPKR(r.overdue_60)}</td>
                    <td style={{ color:r.overdue_90>0?'var(--danger)':undefined }}>{fmtPKR(r.overdue_90)}</td>
                    <td style={{ color:r.overdue_120plus>0?'#7f1d1d':undefined, fontWeight:r.overdue_120plus>0?700:400 }}>{fmtPKR(r.overdue_120plus)}</td>
                    <td style={{ fontWeight:700 }}>{fmtPKR(r.total_outstanding)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ fontWeight:700, background:'var(--gray-50)' }}>
                  <td>Total</td><td>—</td>
                  <td>{fmtPKR(totals.current)}</td><td>{fmtPKR(totals.d30)}</td>
                  <td>{fmtPKR(totals.d60)}</td><td>{fmtPKR(totals.d90)}</td>
                  <td>{fmtPKR(totals.d120)}</td><td>{fmtPKR(totals.total)}</td>
                </tr>
              </tfoot>
            </table>
          ) : <EmptyState icon="📊" title="No outstanding receivables" />}
        </div>
      </div>
    </AppShell>
  );
}

// ── Refunds ───────────────────────────────────────────────────────────────────
export function RefundsPage() {
  const [refunds, setRefunds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ invoiceId:'', amount:'', reason:'' });
  const set = k => e => setForm(f=>({...f,[k]:e.target.value}));

  useEffect(() => {
    api.get('/finance/refunds').then(r=>r.data).then(r=>{ if(r.success) setRefunds(r.data); }).finally(()=>setLoading(false));
  }, []);

  const createRefund = async () => {
    if (!form.invoiceId||!form.amount||!form.reason) { toast.error('All fields required'); return; }
    try {
      const r = await api.post('/finance/refunds', form).then(r=>r.data);
      if (r.success) { toast.success('Refund processed'); setShowCreate(false); }
    } catch(e) { toast.error(e.response?.data?.message||'Failed'); }
  };

  return (
    <AppShell title="Refunds & Credit Notes" actions={
      <button className="btn btn-primary btn-sm" onClick={()=>setShowCreate(true)}><Plus size={14}/> Process Refund</button>
    }>
      <div className="card">
        <div className="table-wrap">
          {loading ? <Spinner /> : refunds.length ? (
            <table>
              <thead><tr><th>Invoice</th><th>Customer</th><th>Amount</th><th>Reason</th><th>Issued By</th><th>Date</th><th>Status</th><th></th></tr></thead>
              <tbody>
                {refunds.map(r=>(
                  <tr key={r.note_id}>
                    <td><code style={{ fontSize:'.78rem' }}>{r.invoice_no}</code></td>
                    <td style={{ fontWeight:500 }}>{r.customer_name}</td>
                    <td style={{ fontWeight:600, color:'var(--danger)' }}>{fmtPKR(r.amount)}</td>
                    <td style={{ maxWidth:200 }}><span className="truncate text-sm" style={{ display:'block' }}>{r.reason}</span></td>
                    <td className="text-sm">{r.issued_by_name||'—'}</td>
                    <td className="text-sm">{fmtDate(r.issued_at)}</td>
                    <td><span className={`badge ${r.status==='ISSUED'?'badge-warning':r.status==='APPLIED'?'badge-success':'badge-gray'}`}>{r.status}</span></td>
                    <td>
                      {r.status==='ISSUED' && (
                        <button className="btn btn-danger btn-sm" style={{ fontSize:'.7rem' }}
                          onClick={async()=>{ if(!window.confirm('Cancel this refund/credit note?')) return; try{ await api.patch('/finance/refunds/'+r.note_id,{ status:'CANCELLED' }); toast.success('Refund cancelled'); const res=await api.get('/finance/refunds').then(x=>x.data); if(res.success) setRefunds(res.data); }catch(ex){ toast.error(ex.response?.data?.message||'Failed'); } }}>
                          <Trash2 size={11}/> Cancel
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : <EmptyState icon="💸" title="No refunds processed" />}
        </div>
      </div>

      <Modal open={showCreate} onClose={()=>setShowCreate(false)} title="Process Refund">
        <div className="modal-body">
          <div className="form-group"><label className="form-label" htmlFor="ref-inv">Invoice ID <span className="req">*</span></label><input id="ref-inv" className="form-control" placeholder="UUID of paid invoice" value={form.invoiceId} onChange={set('invoiceId')}/></div>
          <div className="form-group"><label className="form-label" htmlFor="ref-amt">Refund Amount (PKR) <span className="req">*</span></label><input id="ref-amt" className="form-control" type="number" min="0" value={form.amount} onChange={set('amount')}/></div>
          <div className="form-group"><label className="form-label" htmlFor="ref-rsn">Reason <span className="req">*</span></label><textarea id="ref-rsn" className="form-control" rows={3} value={form.reason} onChange={set('reason')}/></div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={()=>setShowCreate(false)}>Cancel</button>
          <button className="btn btn-primary" onClick={createRefund}>Process Refund</button>
        </div>
      </Modal>
    </AppShell>
  );
}
